import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["commandName", "commandDesc", "terminalBody", "playPause", "pauseIcon", "playIcon", "scenesData"]
  static values = { pauseLabel: String, playLabel: String }

  connect() {
    this.currentScene = 0
    this.playing = true
    this.animating = false
    this._timeouts = []
    this._scenes = JSON.parse(this.scenesDataTarget.textContent)
    this._lockHeight()

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this._showAll()
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.observer.disconnect()
            this._playScene(0)
          }
        })
      },
      { threshold: 0.3 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this._clearTimeouts()
    this.terminalBodyTarget.style.minHeight = ""
  }

  togglePlayPause() {
    if (this.playing) {
      this.playing = false
      this._clearTimeouts()
      this.pauseIconTarget.classList.add("is-hidden")
      this.playIconTarget.classList.remove("is-hidden")
      this.playPauseTarget.setAttribute("aria-label", this.playLabelValue)
    } else {
      this.playing = true
      this.pauseIconTarget.classList.remove("is-hidden")
      this.playIconTarget.classList.add("is-hidden")
      this.playPauseTarget.setAttribute("aria-label", this.pauseLabelValue)
      // If not currently animating, advance to next scene
      if (!this.animating) {
        this._nextScene()
      }
    }
  }

  // --- Private ---

  _playScene(index) {
    this.currentScene = index
    this.animating = true
    const scene = this._scenes[index]

    // Update command header with fade (optional targets for Connect page)
    if (this.hasCommandNameTarget) {
      this.commandNameTarget.style.opacity = "0"
      this.commandDescTarget.style.opacity = "0"

      this._delay(150, () => {
        this.commandNameTarget.textContent = scene.command
        this.commandDescTarget.textContent = scene.desc
        this.commandNameTarget.style.opacity = "1"
        this.commandDescTarget.style.opacity = "1"
      })
    }

    // Clear terminal
    this.terminalBodyTarget.innerHTML = ""

    // Build line elements
    const lineEls = scene.lines.map((text, i) => {
      const type = scene.types[i] || "text"
      return this._createLine(text, type)
    })

    // Animate lines sequentially
    this._animateLines(lineEls, scene.types, 0, () => {
      this.animating = false
      if (this.playing) {
        this._delay(4000, () => {
          if (this.playing) this._nextScene()
        })
      }
    })
  }

  _animateLines(lineEls, types, index, done) {
    if (index >= lineEls.length) { done(); return }

    const el = lineEls[index]
    const type = types[index] || "text"

    if (type === "result-card") {
      // Append result-card into the results group container
      let group = this.terminalBodyTarget.querySelector(".demo-results-group")
      if (!group) {
        group = document.createElement("div")
        group.className = "demo-results-group"
        this.terminalBodyTarget.appendChild(group)
      }
      group.appendChild(el)
    } else {
      this.terminalBodyTarget.appendChild(el)
    }

    if (type === "input" || type === "chat-user") {
      // Typing animation for input / chat-user lines
      const fullText = el.dataset.fullText
      const textSpan = el.querySelector(".demo-line-text")
      const cursor = el.querySelector(".demo-cursor")
      textSpan.textContent = ""
      el.classList.add("is-visible")
      if (cursor) cursor.classList.add("is-typing")

      this._typeText(textSpan, fullText, 40, () => {
        if (cursor) {
          cursor.classList.remove("is-typing")
          cursor.classList.add("is-done")
        }
        this._delay(300, () => {
          this._animateLines(lineEls, types, index + 1, done)
        })
      })
    } else if (type === "blank") {
      el.classList.add("is-visible")
      this._delay(100, () => {
        this._animateLines(lineEls, types, index + 1, done)
      })
    } else {
      // Fade in for all other types
      this._delay(50, () => {
        el.classList.add("is-visible")
        this._delay(200, () => {
          this._animateLines(lineEls, types, index + 1, done)
        })
      })
    }
  }

  _typeText(el, text, speed, callback) {
    let i = 0
    const tick = () => {
      if (i < text.length) {
        el.textContent += text[i]
        i++
        this._delay(speed, tick)
      } else {
        callback()
      }
    }
    tick()
  }

  _createLine(text, type) {
    const div = document.createElement("div")
    div.className = `demo-line demo-line-${type}`

    if (type === "blank") {
      div.innerHTML = "&nbsp;"
      return div
    }

    if (type === "input") {
      // Split "> " prefix (green) from the rest
      const prefix = text.startsWith("> ") ? "> " : ""
      const rest = prefix ? text.slice(2) : text
      div.dataset.fullText = rest
      div.innerHTML =
        `<span class="demo-prefix-prompt">${this._escapeHtml(prefix)}</span>` +
        `<span class="demo-line-text"></span>` +
        `<span class="demo-cursor"></span>`
      return div
    }

    if (type === "chat-user") {
      div.dataset.fullText = text
      div.innerHTML =
        `<span class="demo-avatar demo-avatar-user">Y</span>` +
        `<span class="demo-chat-body">` +
          `<span class="demo-line-text"></span>` +
          `<span class="demo-cursor"></span>` +
        `</span>`
      return div
    }

    if (type === "chat-ai") {
      div.innerHTML =
        `<span class="demo-avatar demo-avatar-ai">C</span>` +
        `<span class="demo-chat-body">${this._escapeHtml(text)}</span>`
      return div
    }

    if (type === "tool-call") {
      div.innerHTML =
        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>` +
        `<span>${this._escapeHtml(text)}</span>`
      return div
    }

    if (type === "result-card") {
      // "title||body||tags" separated by ||
      const parts = text.split("||")
      const title = parts[0] || ""
      const body = parts[1] || ""
      const tags = parts[2] || ""
      div.innerHTML =
        `<div class="demo-result-title">${this._escapeHtml(title)}</div>` +
        `<div class="demo-result-body">${this._escapeHtml(body)}</div>` +
        `<div class="demo-result-tags">${this._escapeHtml(tags)}</div>`
      return div
    }

    if (type === "summary") {
      div.textContent = text
      return div
    }

    if (type === "ai") {
      // "AI  " prefix in blue
      const prefix = text.slice(0, 4)
      const rest = text.slice(4)
      div.innerHTML =
        `<span class="demo-prefix-ai">${this._escapeHtml(prefix)}</span>` +
        `<span>${this._escapeHtml(rest)}</span>`
      return div
    }

    if (type === "user") {
      // "Y   " or "Publish all? Y" — find Y prefix
      const match = text.match(/^(Y\s+)(.*)$/)
      if (match) {
        div.innerHTML =
          `<span class="demo-prefix-user">${this._escapeHtml(match[1])}</span>` +
          `<span>${this._escapeHtml(match[2])}</span>`
      } else {
        // "Publish all? Y" style — find trailing Y
        const matchEnd = text.match(/^(.+?\s)(Y)$/)
        if (matchEnd) {
          div.innerHTML =
            `<span>${this._escapeHtml(matchEnd[1])}</span>` +
            `<span class="demo-prefix-user">${this._escapeHtml(matchEnd[2])}</span>`
        } else {
          div.textContent = text
        }
      }
      return div
    }

    if (type === "success") {
      // "✓" in green, rest in green
      div.textContent = text
      return div
    }

    // Default: text, title, tag, url, ai-cont
    div.textContent = text
    return div
  }

  _nextScene() {
    const next = (this.currentScene + 1) % this._scenes.length
    this._playScene(next)
  }

  _showAll() {
    // Reduced motion: show first scene fully, no animation
    const scene = this._scenes[0]

    if (this.hasCommandNameTarget) {
      this.commandNameTarget.textContent = scene.command
      this.commandDescTarget.textContent = scene.desc
    }

    scene.lines.forEach((text, i) => {
      const type = scene.types[i] || "text"
      const el = this._createLine(text, type)

      if (type === "input" || type === "chat-user") {
        const textSpan = el.querySelector(".demo-line-text")
        textSpan.textContent = el.dataset.fullText
        const cursor = el.querySelector(".demo-cursor")
        if (cursor) cursor.classList.add("is-done")
      }
      el.classList.add("is-visible")

      if (type === "result-card") {
        let group = this.terminalBodyTarget.querySelector(".demo-results-group")
        if (!group) {
          group = document.createElement("div")
          group.className = "demo-results-group"
          this.terminalBodyTarget.appendChild(group)
        }
        group.appendChild(el)
      } else {
        this.terminalBodyTarget.appendChild(el)
      }
    })

    // Hide play/pause since no animation
    if (this.hasPlayPauseTarget) {
      this.playPauseTarget.style.display = "none"
    }
  }

  _lockHeight() {
    const body = this.terminalBodyTarget
    let maxHeight = 0

    this._scenes.forEach(scene => {
      body.innerHTML = ""
      scene.lines.forEach((text, i) => {
        const type = scene.types[i] || "text"
        const el = this._createLine(text, type)
        el.classList.add("is-visible")

        if (type === "input" || type === "chat-user") {
          const textSpan = el.querySelector(".demo-line-text")
          textSpan.textContent = el.dataset.fullText
        }

        if (type === "result-card") {
          let group = body.querySelector(".demo-results-group")
          if (!group) {
            group = document.createElement("div")
            group.className = "demo-results-group"
            body.appendChild(group)
          }
          group.appendChild(el)
        } else {
          body.appendChild(el)
        }
      })
      maxHeight = Math.max(maxHeight, body.scrollHeight)
    })

    body.innerHTML = ""
    body.style.minHeight = `${maxHeight}px`
  }

  _delay(ms, fn) {
    const id = setTimeout(fn, ms)
    this._timeouts.push(id)
    return id
  }

  _clearTimeouts() {
    this._timeouts.forEach(id => clearTimeout(id))
    this._timeouts = []
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
